import { z } from 'zod';

import { telefonoSchema } from './usuarios';

/**
 * Recuperar la contraseña con un código al teléfono.
 *
 * ## La respuesta es siempre la misma
 *
 * Exista el número o no, se contesta lo mismo. Si dijera «ese número no está
 * registrado», cualquiera podría averiguar quién vive en el fraccionamiento
 * probando números — y eso es justo lo que un control de acceso existe para no
 * revelar.
 *
 * Es incómodo para quien se equivoca al teclear su propio número, y ese
 * incomodo se paga a cambio de no publicar el padrón.
 */

/** Cuántos dígitos. Seis es lo que la gente sabe teclear de un mensaje. */
export const CODIGO_DIGITOS = 6;

/**
 * Cuánto vale un código.
 *
 * Diez minutos: suficiente para leer el mensaje, volver a la app y teclearlo;
 * corto para que uno interceptado sirva de poco.
 */
export const CODIGO_VIGENCIA_MS = 10 * 60_000;

/**
 * Cuántas veces se puede fallar antes de invalidarlo.
 *
 * Seis dígitos son un millón de combinaciones: sin tope se adivinan en minutos
 * con un script. Con tres, acertar a ciegas es tres entre un millón.
 */
export const CODIGO_INTENTOS = 3;

/**
 * El código: exactamente los dígitos que dice `CODIGO_DIGITOS`.
 *
 * **Escrita entera y no armada con una plantilla.** Estaba como
 * `` new RegExp(`^\d{${CODIGO_DIGITOS}}$`) ``, y dentro de una plantilla `\d`
 * no es un escape que JavaScript conozca: se queda en una `d` suelta. La
 * expresión resultante era `^d{6}$` — seis letras «d»— así que **ningún código
 * pasaba la validación**, y el error decía «el código son 6 dígitos» sobre un
 * código que tenía seis dígitos.
 *
 * La prueba de abajo la ata a la constante, que es lo que se perdía al
 * escribirla a mano.
 */
const SOLO_DIGITOS = /^\d{6}$/;

/**
 * Por dónde se manda el código.
 *
 * WhatsApp se lee mejor y no lo filtra ninguna operadora; SMS llega a cualquier
 * teléfono, tenga o no WhatsApp. Ninguno de los dos gana siempre, y por eso hay
 * los dos.
 */
export const canalCodigoSchema = z.enum(['SMS', 'WHATSAPP']);
export type CanalCodigo = z.infer<typeof canalCodigoSchema>;

export const pedirCodigoSchema = z.object({
  telefono: telefonoSchema,
  /**
   * Lo pide quien ya esperó y no le llegó.
   *
   * **Por qué el respaldo lo dispara una persona y no el servidor:** Twilio
   * contesta «aceptado» a un WhatsApp enseguida, y el resultado de verdad —que
   * el destinatario no tenga WhatsApp, por ejemplo— llega después y por
   * separado. Un «si falla, manda SMS» automático creería que salió, y la
   * persona se quedaría esperando.
   *
   * Quien está mirando el teléfono sabe si llegó; el servidor no. Y de paso
   * solo se paga el segundo mensaje cuando de verdad hizo falta.
   */
  canal: canalCodigoSchema.optional(),
});

export type PedirCodigo = z.infer<typeof pedirCodigoSchema>;

export const confirmarCodigoSchema = z.object({
  telefono: telefonoSchema,
  codigo: z.string().trim().regex(SOLO_DIGITOS, `El código son ${CODIGO_DIGITOS} dígitos`),
  passwordNueva: z.string().min(8, 'Al menos 8 caracteres').max(72),
});

export type ConfirmarCodigo = z.infer<typeof confirmarCodigoSchema>;

/**
 * Lo que se contesta al pedir un código: siempre lo mismo.
 *
 * Sin un campo que diga si el número existía. Que el tipo no lo tenga es lo que
 * impide que alguien lo añada «para mejorar el mensaje» sin darse cuenta de lo
 * que revela.
 */
export interface CodigoPedido {
  mensaje: string;
  /**
   * Por dónde salió.
   *
   * No revela nada: se contesta igual exista el número o no. Sirve para que la
   * pantalla diga «revisa tu WhatsApp» en vez de «revisa tus mensajes», y para
   * saber si tiene sentido ofrecer el respaldo.
   */
  canal: CanalCodigo;
}
